/**
 * Состояние инкрементального pull'а для useEntityList — без React, чтобы
 * покрываться unit-тестами (node --test).
 *
 * Сервер (`GenericSyncService.pull`) отдаёт записи с serverTimestamp > since.
 * Если с прошлого pull ничего не менялось, приходит ПУСТОЙ список — это
 * «изменений нет», а не «записей нет». Инкрементальный ответ всегда мержится
 * с кэшем; полный (since = 0) — заменяет его.
 */
import type { EntityType, SyncEntity } from '../types/entities'

// serverTimestamp последнего pull по каждому entity_type (живёт всю сессию вкладки)
const sinceMap = new Map<EntityType, number>()

/**
 * Курсор для следующего pull. Если кэша нет (первый заход, кэш очищен при
 * logout или вытеснен gc) — только полный pull с 0: инкремент без базы дал бы
 * неполный список.
 */
export function sinceFor(entity: EntityType, hasCachedData: boolean): number {
  return hasCachedData ? (sinceMap.get(entity) ?? 0) : 0
}

/** Запомнить курсор после успешного pull. */
export function rememberServerTimestamp(entity: EntityType, serverTimestamp: number): void {
  if (serverTimestamp > 0) sinceMap.set(entity, serverTimestamp)
}

/** Сбросить курсор сущности (после push — следующий pull полный) или всех (logout). */
export function resetSince(entity?: EntityType): void {
  if (entity) sinceMap.delete(entity)
  else sinceMap.clear()
}

/**
 * Итоговый список после pull: полный pull заменяет кэш, инкрементальный
 * мержится по id (в т.ч. пустой — кэш остаётся как был).
 */
export function mergePulled<T extends SyncEntity>(
  prev: readonly T[] | undefined,
  pulled: T[],
  since: number,
): T[] {
  if (since <= 0) return pulled
  const byId = new Map((prev ?? []).map((r) => [r.id, r]))
  for (const r of pulled) byId.set(r.id, r)
  return Array.from(byId.values())
}

/**
 * Проверки «запись в web-формате» по сущности. Серверная сущность может
 * содержать записи другого клиента: `attendance` пишут и web (mark + date +
 * classId), и KMP-приложение (status + lessonId). Чужой формат web не
 * отображает и не падает на нём. Общая схема — этап «каноническая схема журнала».
 */
const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0

const webFormatGuards: Partial<Record<EntityType, (r: Record<string, unknown>) => boolean>> = {
  attendance: (r) =>
    isNonEmptyString(r.studentId) &&
    isNonEmptyString(r.classId) &&
    isNonEmptyString(r.date) &&
    isNonEmptyString(r.mark),
}

/** Подходит ли запись (уже прошедшая базовую валидацию) под web-формат сущности. */
export function isWebFormat(entity: EntityType, record: Record<string, unknown>): boolean {
  const guard = webFormatGuards[entity]
  return guard ? guard(record) : true
}

/**
 * Отфильтровать записи pull: невалидные базово (`isValid` = Zod-схема) и в
 * чужом формате пропускаются, а не роняют весь список. Возвращает принятые
 * записи и число пропущенных.
 */
export function filterPulled<T extends SyncEntity>(
  entity: EntityType,
  records: readonly unknown[],
  isValid: (r: unknown) => r is T,
): { records: T[]; skipped: number } {
  const accepted: T[] = []
  for (const r of records) {
    if (isValid(r) && isWebFormat(entity, r as unknown as Record<string, unknown>)) accepted.push(r)
  }
  return { records: accepted, skipped: records.length - accepted.length }
}

/** Размер страницы pull (сервер ограничивает 5000, см. GenericSyncService.MAX_PULL_LIMIT). */
export const PULL_PAGE_SIZE = 1000
/** Предохранитель от бесконечной дочитки (1000 × 500 = 500 тыс. записей). */
export const PULL_MAX_PAGES = 500

/**
 * Дочитать все страницы pull начиная с `since`.
 *
 * Сервер отдаёт до `limit` записей (плюс «хвост» группы с тем же
 * server_timestamp) и курсор = max(ts) страницы. Пока страница полная —
 * запрашиваем следующую с новым курсором. Цикл обрывается, если курсор
 * не сдвинулся (защита от зацикливания) или достигнут PULL_MAX_PAGES.
 */
export async function pullAllPages<R>(
  fetchPage: (since: number, limit: number) => Promise<{ records: R[]; serverTimestamp: number }>,
  since: number,
  limit: number = PULL_PAGE_SIZE,
  maxPages: number = PULL_MAX_PAGES,
): Promise<{ records: R[]; serverTimestamp: number }> {
  const all: R[] = []
  let cursor = since
  for (let page = 0; page < maxPages; page++) {
    const resp = await fetchPage(cursor, limit)
    all.push(...resp.records)
    const next = resp.serverTimestamp
    if (resp.records.length < limit || !(next > cursor)) {
      cursor = Math.max(cursor, next)
      break
    }
    cursor = next
  }
  return { records: all, serverTimestamp: cursor }
}
